import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { JanelaBase, LeadsBaseAlcancavel } from '@/types/leadsDashboard';
import { diaCurto, n, pct } from '../format';

/**
 * Bloco 5.4 — Base alcançável por broadcast.
 *
 * Área roxa é o estoque de alcançáveis ao longo do tempo. Linha vermelha
 * tracejada, em eixo próprio à direita, é a taxa de perda diária. Juntas, dá
 * para ver se a base cresce porque entra mais ou porque vaza menos.
 */

const JANELAS: JanelaBase[] = [7, 15, 30, 60, 90];

function Tip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div className="tt">
            <div className="tt-t">{label}</div>
            {payload.map((p: any) => (
                <div className="tt-r" key={p.dataKey}>
                    <span style={{ color: p.color }}>{p.name}</span>
                    <span className="num" style={{ fontWeight: 600 }}>
                        {p.dataKey === 'taxa_perda_pct' ? pct(p.value, 2) : n(p.value)}
                    </span>
                </div>
            ))}
        </div>
    );
}

interface Props {
    dados: LeadsBaseAlcancavel;
    rotulo: string;
    alcancadosPeriodo: number;
    janela: JanelaBase;
    onJanela: (j: JanelaBase) => void;
}

export default function GraficoBaseAlcancavel({
    dados, rotulo, alcancadosPeriodo, janela, onJanela,
}: Props) {
    const serie = dados.serie.map(p => ({ ...p, rotulo: diaCurto(p.data) }));

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Base alcançável por broadcast</div>
                    <div className="card-s">
                        Área é quanta gente poderia receber disparo. Linha vermelha é o % que
                        sai por 551 a cada dia.
                    </div>
                </div>
                <div className="seg">
                    {JANELAS.map(d => (
                        <button
                            key={d} type="button"
                            data-on={janela === d ? 1 : 0}
                            onClick={() => onJanela(d)}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            {serie.length === 0 ? (
                <div className="empty">Sem histórico de base alcançável nesta janela ainda.</div>
            ) : (
                <div style={{ padding: '18px 8px 6px 0' }}>
                    <ResponsiveContainer width="100%" height={198}>
                        <AreaChart data={serie} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gAlcLx" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#6D4AC7" stopOpacity={0.24} />
                                    <stop offset="100%" stopColor="#6D4AC7" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#F0F2F5" vertical={false} />
                            <XAxis
                                dataKey="rotulo"
                                tick={{ fontSize: 11, fill: '#8C93A0' }}
                                axisLine={{ stroke: '#E3E6EB' }} tickLine={false}
                                interval={Math.max(0, Math.floor(janela / 9))}
                            />
                            <YAxis
                                yAxisId="l"
                                tick={{ fontSize: 11, fill: '#8C93A0' }}
                                axisLine={false} tickLine={false} width={54}
                                tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                            />
                            {/* Eixo próprio à direita: a taxa de perda é ordem de
                                grandeza menor que o estoque e sumiria no mesmo eixo. */}
                            <YAxis
                                yAxisId="r" orientation="right"
                                tick={{ fontSize: 11, fill: '#C0392B' }}
                                axisLine={false} tickLine={false} width={48}
                                tickFormatter={(v: number) => `${v.toFixed(2).replace('.', ',')}%`}
                            />
                            <Tooltip content={<Tip />} />
                            <Area
                                yAxisId="l" type="monotone" dataKey="alcancaveis" name="Alcançáveis"
                                stroke="#6D4AC7" strokeWidth={2} fill="url(#gAlcLx)"
                            />
                            <Area
                                yAxisId="r" type="monotone" dataKey="taxa_perda_pct" name="Taxa de perda 551"
                                stroke="#C0392B" strokeWidth={1.6} strokeDasharray="4 3" fill="none"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            <div className="strip">
                <div><span>Alcançáveis hoje</span><b className="num">{n(dados.alcancaveis_hoje)}</b></div>
                <div>
                    <span>Alcançados em {rotulo}</span>
                    <b className="num" style={{ color: 'var(--violet)' }}>{n(alcancadosPeriodo)}</b>
                </div>
                <div>
                    <span>Crescimento em {janela}d</span>
                    <b className={dados.crescimento_pct >= 0 ? 'lx-up' : 'lx-down'}>
                        {dados.crescimento_pct >= 0 ? '+' : ''}{pct(dados.crescimento_pct)}
                    </b>
                </div>
                <div>
                    <span>Ganho líquido</span>
                    <b className="num" style={{ color: dados.ganho_liquido >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {dados.ganho_liquido >= 0 ? '+' : '−'}{n(Math.abs(dados.ganho_liquido))}
                    </b>
                </div>
                <div>
                    <span>Taxa de perda diária</span>
                    <b className="num" style={{ color: 'var(--red)' }}>
                        {pct(dados.taxa_perda_diaria_media_pct, 2)}
                    </b>
                </div>
                <div>
                    <span>Perdidos em {janela}d</span>
                    <b className="num" style={{ color: 'var(--red)' }}>−{n(dados.perdidos_na_janela)}</b>
                </div>
            </div>
        </div>
    );
}
